import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Plus, Save, Settings, SlidersHorizontal } from "lucide-react";
import { api, type FeatureFlag, type Hotel, type LowScoreReason, type LowScoreReasonGroup, type Organization } from "../api/client";

const languages = ["en", "sr-Latn", "ru", "de", "fr", "es", "pt", "he"] as const;

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [editingReasonId, setEditingReasonId] = useState<string | null>(null);
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api<Organization[]>("/api/admin/organizations")
  });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const featureFlags = useQuery({ queryKey: ["feature-flags"], queryFn: () => api<FeatureFlag[]>("/api/admin/feature-flags") });
  const groups = useQuery({
    queryKey: ["low-score-reason-groups"],
    queryFn: () => api<LowScoreReasonGroup[]>("/api/admin/low-score-reason-groups")
  });
  const organization = organizations.data?.[0] ?? null;
  const selectedGroup = useMemo(() => {
    return (groups.data ?? []).find((group) => group.id === selectedGroupId) ?? groups.data?.[0] ?? null;
  }, [groups.data, selectedGroupId]);

  const createGroup = useMutation({
    mutationFn: (body: unknown) => api<LowScoreReasonGroup>("/api/admin/low-score-reason-groups", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (group) => {
      setSelectedGroupId(group.id);
      queryClient.invalidateQueries({ queryKey: ["low-score-reason-groups"] });
    }
  });

  const updateGroup = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<LowScoreReasonGroup>(`/api/admin/low-score-reason-groups/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["low-score-reason-groups"] })
  });

  const createReason = useMutation({
    mutationFn: ({ groupId, body }: { groupId: string; body: unknown }) => api<LowScoreReason>(`/api/admin/low-score-reason-groups/${groupId}/reasons`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["low-score-reason-groups"] })
  });

  const updateReason = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<LowScoreReason>(`/api/admin/low-score-reasons/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => {
      setEditingReasonId(null);
      queryClient.invalidateQueries({ queryKey: ["low-score-reason-groups"] });
    }
  });

  const updateOrganization = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<Organization>(`/api/admin/organizations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizations"] })
  });
  const updateFeatureFlag = useMutation({
    mutationFn: ({ key, body }: { key: string; body: unknown }) => api<FeatureFlag>(`/api/admin/feature-flags/${key}`, {
      method: "PUT",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["feature-flags"] })
  });

  function submitGroupCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createGroup.mutate({
      key: String(form.get("key")),
      name: String(form.get("name")),
      category: String(form.get("category") || "general"),
      active: true
    });
    event.currentTarget.reset();
  }

  function submitGroupUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedGroup) return;
    const form = new FormData(event.currentTarget);
    updateGroup.mutate({
      id: selectedGroup.id,
      body: {
        key: String(form.get("key")),
        name: String(form.get("name")),
        category: String(form.get("category")),
        active: form.get("active") === "on"
      }
    });
  }

  function submitReasonCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedGroup) return;
    const form = new FormData(event.currentTarget);
    createReason.mutate({
      groupId: selectedGroup.id,
      body: {
        value: String(form.get("value")),
        displayOrder: Number(form.get("displayOrder") || selectedGroup.reasons.length * 10),
        active: true,
        translations: translationPayload(form)
      }
    });
    event.currentTarget.reset();
  }

  function submitReasonUpdate(event: FormEvent<HTMLFormElement>, reason: LowScoreReason) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const translations = translationPayload(form);
    updateReason.mutate({
      id: reason.id,
      body: {
        value: String(form.get("value")),
        displayOrder: Number(form.get("displayOrder") || reason.displayOrder),
        active: form.get("active") === "on",
        ...(translations.length ? { translations } : {})
      }
    });
  }

  function submitOrganizationUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organization) return;
    const form = new FormData(event.currentTarget);
    updateOrganization.mutate({
      id: organization.id,
      body: {
        name: String(form.get("name")),
        slug: String(form.get("slug")),
        defaultTimezone: String(form.get("defaultTimezone") || "Europe/Podgorica")
      }
    });
  }

  function submitFeatureFlag(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const key = String(form.get("key"));
    updateFeatureFlag.mutate({
      key,
      body: {
        enabled: form.get("enabled") === "on",
        hotelId: String(form.get("hotelId") || "") || null,
        config: parseJsonObject(String(form.get("config") || "{}"))
      }
    });
  }

  return (
    <div className="page-stack">
      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Organizacija</h2>
            <p>Superadmin odrzava tenant/grupu i default timezone za objekte.</p>
          </div>
        </div>
        {organization ? (
          <form className="inline-form-grid" onSubmit={submitOrganizationUpdate}>
            <label>
              <span>Naziv</span>
              <input name="name" defaultValue={organization.name} required />
            </label>
            <label>
              <span>Slug</span>
              <input name="slug" defaultValue={organization.slug} required pattern="[a-z0-9-]{2,120}" />
            </label>
            <label>
              <span>Default timezone</span>
              <input name="defaultTimezone" defaultValue={organization.defaultTimezone} required />
            </label>
            <button type="submit" disabled={updateOrganization.isPending}><Save size={16} /> Sacuvaj organizaciju</button>
          </form>
        ) : (
          <p>Nema dostupne organizacije za trenutni nalog.</p>
        )}
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Feature flags</h2>
            <p>Ukljuci/iskljuci module po organizaciji ili objektu bez deploy-a.</p>
          </div>
        </div>
        <form className="inline-form-grid" onSubmit={submitFeatureFlag}>
          <label>
            <span>Flag key</span>
            <select name="key" required>
              <option value="qr_verification_mode">qr_verification_mode</option>
              <option value="review_gating_disabled">review_gating_disabled</option>
              <option value="review_flow">review_flow</option>
              <option value="guest_recovery_followup">guest_recovery_followup</option>
              <option value="webhook_integrations">webhook_integrations</option>
              <option value="advanced_comment_analysis">advanced_comment_analysis</option>
            </select>
          </label>
          <label>
            <span>Scope</span>
            <select name="hotelId">
              <option value="">Organizacija</option>
              {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
            </select>
          </label>
          <label>
            <span>Config JSON</span>
            <input name="config" defaultValue="{}" />
          </label>
          <label className="check-control"><input type="checkbox" name="enabled" /> Enabled</label>
          <button type="submit"><SlidersHorizontal size={16} /> Sacuvaj flag</button>
          <p className="form-note">Za privremeno gašenje Google review modal-a izaberi <code>review_gating_disabled</code>, scope objekat i uključi Enabled.</p>
        </form>
        {updateFeatureFlag.error && <p className="form-error">Feature flag nije sacuvan. Provjeri scope i JSON config.</p>}
        <div className="recipient-list">
          {(featureFlags.data ?? []).map((flag) => (
            <article className="recipient-card" key={flag.id}>
              <div>
                <strong>{flag.key}</strong>
                <span>{flag.enabled ? "enabled" : "disabled"} - {flag.scope}</span>
                <small>{flag.hotel?.name ?? "Organizacija"} / {JSON.stringify(flag.config ?? {})}</small>
              </div>
              <Settings size={18} />
            </article>
          ))}
        </div>
      </section>

      <section className="tool-hero card settings-hero">
        <div>
          <span className="panel-eyebrow left">Low Score Settings</span>
          <h2>Quick reasons za lose ocjene i service recovery analitiku.</h2>
          <p>Value ostaje stabilan za analitiku, a label se prevodi po jezicima za guest flow.</p>
        </div>
        <div className="object-brand-card">
          <Settings size={34} />
          <strong>{groups.data?.length ?? 0} grupa</strong>
          <span>stable values + prevodi</span>
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Reason grupe</h2>
              <p>Survey Builder koristi grupu za scale/NPS low-score follow-up.</p>
            </div>
          </div>
          <div className="hotel-list">
            {(groups.data ?? []).map((group) => (
              <button key={group.id} className={selectedGroup?.id === group.id ? "hotel-card selected" : "hotel-card"} onClick={() => setSelectedGroupId(group.id)}>
                <ListChecks size={18} />
                <span>
                  <strong>{group.name}</strong>
                  <small>{group.key} - {group.category} - {group.active ? "active" : "paused"}</small>
                </span>
              </button>
            ))}
          </div>
          <form className="compact-form" onSubmit={submitGroupCreate}>
            <h3><Plus size={16} /> Nova grupa</h3>
            <input name="key" placeholder="stable_key, npr. restaurant" required pattern="[a-z0-9_]{2,80}" />
            <input name="name" placeholder="Naziv grupe" required />
            <input name="category" defaultValue="general" placeholder="category_key" required pattern="[a-z0-9_]{2,80}" />
            <button type="submit"><Plus size={16} /> Dodaj grupu</button>
          </form>
        </section>

        <section className="panel card">
          {selectedGroup ? (
            <>
              <div className="panel-header">
                <div>
                  <h2>{selectedGroup.name}</h2>
                  <p>{selectedGroup.reasons.length} reason opcija. Quick reasons se cuvaju kao stable value.</p>
                </div>
              </div>

              <form className="compact-form" onSubmit={submitGroupUpdate}>
                <h3><Save size={16} /> Uredi grupu</h3>
                <input name="key" defaultValue={selectedGroup.key} required pattern="[a-z0-9_]{2,80}" />
                <input name="name" defaultValue={selectedGroup.name} required />
                <input name="category" defaultValue={selectedGroup.category} required pattern="[a-z0-9_]{2,80}" />
                <label className="check-control"><input type="checkbox" name="active" defaultChecked={selectedGroup.active} /> Active</label>
                <button type="submit"><Save size={16} /> Sacuvaj grupu</button>
              </form>

              <form className="compact-form" onSubmit={submitReasonCreate}>
                <h3><Plus size={16} /> Novi reason</h3>
                <input name="value" placeholder="stable_value, npr. long_wait" required pattern="[a-z0-9_]{2,80}" />
                <input name="displayOrder" type="number" min={0} defaultValue={selectedGroup.reasons.length * 10} />
                <div className="inline-form-grid">
                  {languages.map((language) => (
                    <label key={language}>
                      <span>{language}</span>
                      <input name={`label:${language}`} placeholder={`Label ${language}`} required={language === "sr-Latn"} />
                    </label>
                  ))}
                </div>
                <button type="submit"><Plus size={16} /> Dodaj reason</button>
              </form>

              <div className="recipient-list">
                {selectedGroup.reasons.map((reason) => (
                  <article className="recipient-card" key={reason.id}>
                    {editingReasonId === reason.id ? (
                      <form className="sub-location-edit-form" onSubmit={(event) => submitReasonUpdate(event, reason)}>
                        <div className="inline-form-grid">
                          <label>
                            <span>Value</span>
                            <input name="value" defaultValue={reason.value} required pattern="[a-z0-9_]{2,80}" />
                          </label>
                          <label>
                            <span>Order</span>
                            <input name="displayOrder" type="number" min={0} defaultValue={reason.displayOrder} />
                          </label>
                          {languages.map((language) => (
                            <label key={language}>
                              <span>{language}</span>
                              <input name={`label:${language}`} defaultValue={labelFor(reason, language)} placeholder={`Label ${language}`} />
                            </label>
                          ))}
                        </div>
                        <label className="check-control"><input type="checkbox" name="active" defaultChecked={reason.active} /> Active</label>
                        <div className="row-actions">
                          <button type="submit" disabled={updateReason.isPending}><Save size={14} /> Sacuvaj</button>
                          <button type="button" onClick={() => setEditingReasonId(null)}>Cancel</button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div>
                          <strong>{labelFor(reason, "sr-Latn") || reason.value}</strong>
                          <span>{reason.value} - order {reason.displayOrder}</span>
                          <small>{reason.active ? "active" : "paused"} / EN: {labelFor(reason, "en") || "missing"}</small>
                        </div>
                        <div className="row-actions">
                          <button type="button" onClick={() => setEditingReasonId(reason.id)}>Edit</button>
                          <button type="button" onClick={() => updateReason.mutate({ id: reason.id, body: { active: !reason.active } })}>
                            {reason.active ? "Pause" : "Activate"}
                          </button>
                        </div>
                      </>
                    )}
                  </article>
                ))}
              </div>
            </>
          ) : (
            <p>Nema low-score reason grupa.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function labelFor(reason: LowScoreReason, language: string) {
  return reason.translations.find((translation) => translation.language === language)?.label ?? "";
}

function translationPayload(form: FormData) {
  return languages
    .map((language) => ({
      language,
      label: String(form.get(`label:${language}`) || "").trim()
    }))
    .filter((translation) => translation.label);
}

function parseJsonObject(value: string) {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
